/* @refresh skip */
import { useCallback, useMemo } from "react";
import { useLockPositions, useStakingPosition, useUnwindingStore, useWalletBalances } from "../stores";
import { formatUnits } from "viem/utils";
import { useProtocolPNL } from "../stores/useWalletAPYStore";

export const useUserTvl = () => {
    const { tokenBalances, isLoading: isWalletLoading } = useWalletBalances();
    const { stakingPosition, isLoading: isStakingLoading } = useStakingPosition();
    const { lockedPositions, isLoading: isLockedLoading } = useLockPositions();
    const { unwindingPositions, totalUnwindingRewards, isLoading: isUnwindingLoading } = useUnwindingStore();
    const { totalRewards: userTotalRewards, isLoading: isUnwindingProfitLoading } = useProtocolPNL();

    const isLoading =
        isWalletLoading || isStakingLoading || isLockedLoading || isUnwindingLoading || isUnwindingProfitLoading;
    const safeLockedPositions = lockedPositions ?? [];
    const safeUnwindingPositions = unwindingPositions ?? [];

    const iUsdBalance = useMemo(
        () => Number(tokenBalances?.IUSD?.tokenBalance ?? 0),
        [tokenBalances?.IUSD?.tokenBalance],
    );

    const stakedIusdBalance = useMemo(
        () => Number(stakingPosition?.stakedBalance ?? 0),
        [stakingPosition?.stakedBalance],
    );
    const siUsdBalance = useMemo(
        () => Number(tokenBalances?.SIUSD?.tokenBalance ?? 0),
        [tokenBalances?.SIUSD?.tokenBalance],
    );
    const siUsdRewards = useMemo(() => Number(stakingPosition?.rewardsEarned ?? 0), [stakingPosition?.rewardsEarned]);

    const liUsdBalance = useMemo(
        () =>
            safeLockedPositions.reduce(
                (acc, position) => acc + Number(formatUnits(BigInt(position.iUSDBalance), 18) || 0),
                0,
            ),
        [safeLockedPositions],
    );
    const liUsdRewards = useMemo(
        () => safeLockedPositions.reduce((acc, position) => acc + Number(position.rewardsEarned || 0), 0),
        [safeLockedPositions],
    );

    const unwindingBalance = useMemo(
        () => safeUnwindingPositions.reduce((acc, position) => acc + Number(position.lockedBalance || 0), 0),
        [safeUnwindingPositions],
    );

    const unwindingRewards = useMemo(() => totalUnwindingRewards, [totalUnwindingRewards]);

    const liUsdBalanceByDuration = useCallback<(duration: number) => number>(
        (duration: number) =>
            safeLockedPositions
                .filter((position) => position.lockDuration === `${duration}`)
                .reduce((acc, position) => acc + Number(formatUnits(BigInt(position.iUSDBalance), 18) || 0), 0),
        [safeLockedPositions],
    );

    const userTotalTvl = useMemo<number>(() => {
        if (isLoading || !tokenBalances) return 0;
        return iUsdBalance + stakedIusdBalance + liUsdBalance + unwindingBalance + unwindingRewards;
    }, [isLoading, tokenBalances, iUsdBalance, stakedIusdBalance, liUsdBalance, unwindingBalance, unwindingRewards]);

    return {
        isLoading:
            isWalletLoading || isStakingLoading || isLockedLoading || isUnwindingLoading || isUnwindingProfitLoading,
        userTotalTvl,
        userTotalRewards,
        iUsdBalance,
        stakedIusdBalance,
        siUsdBalance,
        siUsdRewards,
        liUsdBalance,
        liUsdRewards,
        unwindingBalance,
        unwindingRewards,
        totalUnwindingRewards,
        liUsdBalanceByDuration,
    };
};
